import { create } from "zustand";
import { toast } from "react-toastify";
import axiosInstance from "../../../../../Hooks/axoisInstance";
import { API_BASE_URL, API_BASE_URL3 } from "../../../../Services/baseApiUrl";

const getFileNameFromDisposition = (disposition, fallback) => {
  if (!disposition) return fallback;
  const match = disposition.match(
    /filename\*?=(?:UTF-8''|")?([^";]+)"?/i
  );
  if (!match?.[1]) return fallback;
  try {
    return decodeURIComponent(match[1].replace(/['"]/g, "").trim());
  } catch {
    return match[1].replace(/['"]/g, "").trim() || fallback;
  }
};

const normalizeIncorrectData = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.incorrectData)) return data.incorrectData;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

export const useEmployeeBulkUploadStore = create((set) => ({
  isBulkUploadLoading: false,
  isSaving: false,
  isSampleDownloading: false,
  showBulkUploadModal: false,
  bulkUploadStep: "form",
  incorrectData: [],

  openBulkUploadModal: () => {
    set({
      showBulkUploadModal: true,
      bulkUploadStep: "form",
      incorrectData: [],
      isSaving: false,
    });
  },

  downloadEmployeeSampleFile: async () => {
    set({ isSampleDownloading: true });
    try {
      const response = await axiosInstance.get(
        `${API_BASE_URL3}MasterDetails/GetEmployeeSampleFile`,
        {
          responseType: "blob",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const fileName = getFileNameFromDisposition(
        response.headers?.["content-disposition"],
        "Employee_Bulk_Upload_Sample.xlsx"
      );

      const blob = new Blob([response.data]);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to download sample file"
      );
      return { success: false };
    } finally {
      set({ isSampleDownloading: false });
    }
  },

  bulkUploadEmployee: async (file) => {
    set({ isBulkUploadLoading: true });
    try {
      const formData = new FormData();
      formData.append("file", file);

      const uploadResponse = await axiosInstance.post(
        `${API_BASE_URL}EmployeeUpload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (uploadResponse.status !== 200) {
        toast.error("Upload failed");
        set({ isBulkUploadLoading: false });
        return { success: false };
      }

      const incorrectResponse = await axiosInstance.get(
        `${API_BASE_URL}EmployeeIncorrect`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const incorrectData = normalizeIncorrectData(incorrectResponse?.data);

      set({
        incorrectData,
        showBulkUploadModal: true,
        bulkUploadStep: "result",
        isBulkUploadLoading: false,
      });

      toast.success(
        uploadResponse?.data?.message || "Employee file uploaded successfully"
      );
      return { success: true };
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.response?.data ||
        "Upload failed";
      toast.error(typeof msg === "string" ? msg : "Upload failed");
      set({ isBulkUploadLoading: false });
      return { success: false };
    }
  },

  saveEmployeeBulkUpload: async () => {
    set({ isSaving: true });
    try {
      const response = await axiosInstance.post(
        `${API_BASE_URL}SaveEmployeeBulkUpload`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.status === 200) {
        toast.success(response?.data?.message || "Saved successfully");
        set({
          showBulkUploadModal: false,
          bulkUploadStep: "form",
          incorrectData: [],
          isSaving: false,
        });
        return { success: true };
      }

      toast.error("Something went wrong");
      set({ isSaving: false });
      return { success: false };
    } catch (error) {
      toast.error(
        error?.response?.data?.message || error?.message || "Save failed"
      );
      set({ isSaving: false });
      return { success: false };
    }
  },

  closeBulkUploadModal: () => {
    set({
      showBulkUploadModal: false,
      bulkUploadStep: "form",
      incorrectData: [],
      isSaving: false,
    });
  },
}));
